import React, { useRef, useEffect } from 'react';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import {
  withStyles,
  MuiThemeProvider,
  createTheme,
} from '@material-ui/core/styles';
import ReactToPrint from 'react-to-print';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import NavigateNext from '@material-ui/icons/NavigateNext';
import { useTranslation } from 'react-i18next';
import { object } from 'prop-types';
import headerBackground from '../Images/header-background.png';
import Footer from '../components/Footer';
import ApplicationForm from '../../forms/Application/PrintableForm';
import {
  getData,
  isFetching as isAppFetching,
} from '../../../state/reducers/ApplicationReducer';
import {
  getQuote,
  isFetching as isQuoteFetching,
} from '../../../state/reducers/PaymentReducer';
import ApplicationActions from '../../../state/actions/ApplicationActions';
import PaymentActions from '../../../state/actions/PaymentActions';
import { isLAO, getEmail } from '../../../state/reducers/LoginReducer';

let styles = theme => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
    minWidth: 350,
    maxWidth: 1000,
  },
  button: {
    marginTop: theme.spacing(2),
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleStyle: {
    color: '#ffffff',
    fontWeight: 'bold',
  },
  subTitle: {
    color: '#4CAF50',
    fontWeight: 'bold',
  },
  warningText: {
    color: '#4CAF50',
  },
  idText: {
    marginTop: theme.spacing(4),
    color: '#000000',
    fontWeight: 'bold',
  },
  emailText: {
    marginTop: theme.spacing(4),
    color: '#000000',
  },
  infoText: {
    display: 'flex',
    alignItems: 'center',
  },
  buttonApplications: {
    fontWeight: 'bold',
    color: '#F9F9F9',
    backgroundColor: '#357a38',
    margin: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(1.1),
    paddingBottom: theme.spacing(1.1),
    fontSize: 18,
  },
  itemMidContainer: {
    marginTop: theme.spacing(10),
  },
  loading: {
    color: '#e0e0e0',
  },
  centered: {
    height: '100%',
    position: 'absolute',
  },
});

let theme = createTheme({
  palette: {
    primary: {
      main: '#6fbf73',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f50057',
      contrastText: '#ffffff',
    },
  },
  typography: {
    useNextVariants: true,
  },
});

let sectionStyle = {
  height: '345px',
  backgroundImage: `url(${headerBackground})`,
};

function PaymentSuccessful(props) {
  const {
    classes,
    quote,
    application,
    isLAO,
    userEmail,
    reset,
    getApplication,
    isAppFetching,
    getPaymentInfo,
    isQuoteFetching,
    getZeroPaymentInfo,
    state,
  } = props;

  let ref = useRef();
  let { push } = useHistory();
  let { uuid } = useParams();
  let { t } = useTranslation();

  useEffect(() => {
    if (uuid) {
      getApplication(uuid);
      getPaymentInfo(uuid);
      getZeroPaymentInfo(uuid);
    }
  }, [getApplication, getPaymentInfo, uuid]);

  if (!uuid) {
    return <Redirect to={'/applications'} />;
  }

  if (isAppFetching || isQuoteFetching) {
    return (
      <Grid
        container
        alignItems="center"
        justifyContent="center"
        direction="row"
        classes={{ container: classes.centered }}
      >
        <Typography className={classes.loading} variant="h5" align="center">
          {t('pleaseWait')}
        </Typography>
      </Grid>
    );
  }
  let { paidByShortCode, feeAmount } = application;
  let email =
    (quote &&
      (quote.authorityId
        ? userEmail
        : paidByShortCode
        ? quote.applicantEmail
        : application.email)) ||
    application.email;
  const isZeroPayment = state.application.zeroPayment.isTotalPaymentCurZero;

  return (
    <div>
      <Grid
        container
        alignItems="center"
        justifyContent="center"
        direction="row"
      >
        <Grid
          className={classes.headerContainer}
          style={sectionStyle}
          item
          xs={12}
        >
          <Typography
            className={classes.titleStyle}
            variant="h3"
            align="center"
            gutterBottom
          >
            {t('onlineServices')}
          </Typography>
        </Grid>
        <Grid className={classes.itemMidContainer}>
          <Paper className={classes.root} elevation={3}>
            <Typography
              className={classes.subTitle}
              align="center"
              variant="h4"
            >
              {!isZeroPayment
                ? t('paymentSuccessful')
                : t('applicationSuccessful')}
            </Typography>
            <Typography
              className={classes.warningText}
              align="center"
              variant="h5"
            >
              {t('applicationSubmitted')}
            </Typography>
            <Typography className={classes.idText} align="center" variant="h6">
              {t('yourApplicationID')}
              {`${application.ian}`}
            </Typography>
            {isLAO && (
              <Typography
                className={classes.idText}
                align="center"
                variant="h6"
              >
                {t('shortcode')}
                {`${application.shortCode}`}
              </Typography>
            )}
            <Typography
              className={classes.emailText}
              align="center"
              variant="h5"
            >
              {t('emailSentTo')}
              {email || 'your email address'}
              {t('withApplicationID')}
            </Typography>
            <Typography
              className={classes.emailText}
              align="left"
              variant="body2"
            >
              {t('whatsNext')}
            </Typography>
          </Paper>
          <Grid container direction="row">
            <Typography className={classes.infoText} variant="subtitle1">
              {t('applicationTracked')}
            </Typography>
            <MuiThemeProvider theme={theme}>
              <ReactToPrint
                trigger={() => (
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.buttonApplications}
                  >
                    {t('print')}
                  </Button>
                )}
                content={() => ref.current}
              />
            </MuiThemeProvider>
            <MuiThemeProvider theme={theme}>
              <Button
                variant="contained"
                color="primary"
                className={classes.buttonApplications}
                onClick={() => {
                  push('/applications');
                  reset();
                }}
              >
                {t('goToApplications')}
                <NavigateNext className={classes.rightIcon} />
              </Button>
            </MuiThemeProvider>
          </Grid>
        </Grid>
      </Grid>
      <Footer />
      <div style={{ display: 'none' }}>
        <div ref={ref}>
          <ApplicationForm application={application} quote={quote} />
        </div>
      </div>
    </div>
  );
}

PaymentSuccessful.propTypes = {
  classes: object.isRequired,
};

let {
  getApplicationRequest,
  clearCurrentApplication,
  checkZeroPayment,
} = ApplicationActions;
let { getPaymentInfoRequest } = PaymentActions;

export default compose(
  withStyles(styles),
  connect(
    s => ({
      quote: getQuote(s),
      isQuoteFetching: isQuoteFetching(s),
      application: getData(s),
      isAppFetching: isAppFetching(s),
      isLAO: isLAO(s),
      userEmail: getEmail(s),
      state: s,
    }),
    {
      getPaymentInfo: getPaymentInfoRequest,
      getApplication: getApplicationRequest,
      reset: clearCurrentApplication,
      getZeroPaymentInfo: checkZeroPayment,
    }
  )
)(PaymentSuccessful);



// WEBPACK FOOTER //
// ./src/view/scenes/Payment/PaymentSuccess.js